import { useMemo, useState } from 'react';
import { Search, ChevronDown, X, Filter, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/Popover';
import {
  HANDLER_TYPE_FILTERS,
  STATUS_LABEL,
  TASK_STATUSES,
  countByHandlerSelections,
  type TaskFilters,
  type TaskStatus,
} from './types';

interface TaskFilterBarProps {
  filters: TaskFilters;
  knownHandlers: string[];
  /** Map handler → target_slugs únicos vistos no cache. */
  targetsByHandler: Record<string, string[]>;
  onChange: (next: Partial<TaskFilters>) => void;
  onClear: () => void;
}

const PERIOD_LABEL: Record<TaskFilters['donePeriod'], string> = {
  '24h': 'Últimas 24h',
  '7d': 'Últimos 7d',
  '30d': 'Últimos 30d',
  all: 'Todas',
};

export function TaskFilterBar({
  filters,
  knownHandlers,
  targetsByHandler,
  onChange,
  onClear,
}: TaskFilterBarProps) {
  const handlerTypeSelectionsTotal = countByHandlerSelections(filters.byHandler);
  const hasActive = useMemo(
    () =>
      filters.q.trim().length > 0 ||
      filters.status.length > 0 ||
      filters.handler.length > 0 ||
      handlerTypeSelectionsTotal > 0 ||
      filters.donePeriod !== '24h' ||
      filters.hideOrphans,
    [filters, handlerTypeSelectionsTotal],
  );

  function toggleByHandler(handler: string, value: string) {
    const current = filters.byHandler[handler] ?? [];
    const nextList = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const nextByHandler = { ...filters.byHandler, [handler]: nextList };
    if (nextList.length === 0) delete nextByHandler[handler];
    onChange({ byHandler: nextByHandler });
  }

  function clearByHandler(handler: string) {
    const next = { ...filters.byHandler };
    delete next[handler];
    onChange({ byHandler: next });
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-raios-muted pointer-events-none" />
          <Input
            value={filters.q}
            onChange={(e) => onChange({ q: e.target.value })}
            placeholder="Buscar id, title, target, handler…"
            className="pl-8 h-9 text-sm"
            aria-label="Buscar tasks"
          />
          {filters.q && (
            <button
              onClick={() => onChange({ q: '' })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-raios-muted hover:text-raios-text"
              aria-label="Limpar busca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Status */}
        <MultiSelectDropdown
          label="Status"
          options={TASK_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
          selected={filters.status}
          onToggle={(v) =>
            onChange({
              status: filters.status.includes(v as TaskStatus)
                ? filters.status.filter((s) => s !== v)
                : [...filters.status, v as TaskStatus],
            })
          }
          onClear={() => onChange({ status: [] })}
        />

        {/* Handler */}
        <MultiSelectDropdown
          label="Handler"
          options={knownHandlers.map((h) => ({ value: h, label: h }))}
          selected={filters.handler}
          onToggle={(v) =>
            onChange({
              handler: filters.handler.includes(v)
                ? filters.handler.filter((h) => h !== v)
                : [...filters.handler, v],
            })
          }
          onClear={() => onChange({ handler: [] })}
          searchable
        />

        {/* Filtros tipo separados — só renderiza se há targets pra aquele handler */}
        {HANDLER_TYPE_FILTERS.map((meta) => {
          const targets = targetsByHandler[meta.handler] ?? [];
          if (targets.length === 0) return null;
          const selected = filters.byHandler[meta.handler] ?? [];
          return (
            <MultiSelectDropdown
              key={meta.handler}
              label={meta.label}
              options={targets.map((t) => ({ value: t, label: t }))}
              selected={selected}
              onToggle={(v) => toggleByHandler(meta.handler, v)}
              onClear={() => clearByHandler(meta.handler)}
              searchable
            />
          );
        })}

        {/* Period */}
        <PeriodSelector
          value={filters.donePeriod}
          onChange={(v) => onChange({ donePeriod: v })}
        />

        {/* P1.13 F7 — Toggle pra esconder tasks órfãs (parent_task_id+FK NULL) */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange({ hideOrphans: !filters.hideOrphans })}
          aria-pressed={filters.hideOrphans}
          title={
            filters.hideOrphans
              ? 'Mostrando: órfãs escondidas. Clique pra exibir.'
              : 'Mostrando todas. Clique pra esconder órfãs (sub-tasks com FK NULL).'
          }
          className={cn(
            'h-9 gap-1.5 border border-raios-elevated/40',
            filters.hideOrphans && 'border-raios-warning/40 text-raios-warning-text',
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {filters.hideOrphans ? 'Órfãs escondidas' : 'Esconder órfãs'}
        </Button>

        {hasActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-raios-muted hover:text-raios-text"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      {/* Active filters chips */}
      {hasActive && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="text-raios-muted inline-flex items-center gap-1">
            <Filter className="h-3 w-3" />
            Ativos:
          </span>
          {filters.q && (
            <ActiveChip label={`"${filters.q}"`} onRemove={() => onChange({ q: '' })} />
          )}
          {filters.status.map((s) => (
            <ActiveChip
              key={`s-${s}`}
              label={`status: ${STATUS_LABEL[s]}`}
              onRemove={() => onChange({ status: filters.status.filter((x) => x !== s) })}
            />
          ))}
          {filters.handler.map((h) => (
            <ActiveChip
              key={`h-${h}`}
              label={`handler: ${h}`}
              onRemove={() => onChange({ handler: filters.handler.filter((x) => x !== h) })}
            />
          ))}
          {Object.entries(filters.byHandler).flatMap(([handler, values]) =>
            values.map((v) => {
              const meta = HANDLER_TYPE_FILTERS.find((m) => m.handler === handler);
              const label = meta?.label.toLowerCase() ?? handler;
              return (
                <ActiveChip
                  key={`bh-${handler}-${v}`}
                  label={`${label}: ${v}`}
                  onRemove={() => toggleByHandler(handler, v)}
                />
              );
            }),
          )}
          {filters.donePeriod !== '24h' && (
            <ActiveChip
              label={`done: ${PERIOD_LABEL[filters.donePeriod]}`}
              onRemove={() => onChange({ donePeriod: '24h' })}
            />
          )}
          {filters.hideOrphans && (
            <ActiveChip
              label="órfãs escondidas"
              onRemove={() => onChange({ hideOrphans: false })}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface MultiSelectDropdownProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  searchable?: boolean;
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  onToggle,
  onClear,
  searchable = false,
}: MultiSelectDropdownProps) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-9 gap-1.5 border border-raios-elevated/40',
            selected.length > 0 && 'border-raios-accent/40 text-raios-accent-text',
          )}
        >
          {label}
          {selected.length > 0 && (
            <span className="px-1 py-0.5 text-[10px] rounded bg-raios-accent/20 text-raios-accent-text tabular-nums">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2 max-h-[60vh] overflow-y-auto">
        {searchable && (
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrar…"
            className="h-7 text-xs mb-2"
            aria-label="Filtrar opções"
          />
        )}
        {filtered.length === 0 ? (
          <p className="text-[11px] text-raios-muted text-center py-3">Sem opções</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <li key={opt.value}>
                  <label className="flex items-center gap-2 px-2 py-1.5 rounded text-xs text-raios-text hover:bg-raios-elevated/50 cursor-pointer">
                    <Checkbox checked={checked} onCheckedChange={() => onToggle(opt.value)} />
                    <span className="truncate">{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {selected.length > 0 && (
          <div className="border-t border-raios-elevated/40 mt-2 pt-2">
            <Button variant="ghost" size="sm" onClick={onClear} className="w-full justify-center text-[11px]">
              Limpar seleção
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface PeriodSelectorProps {
  value: TaskFilters['donePeriod'];
  onChange: (v: TaskFilters['donePeriod']) => void;
}

function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  const periods: Array<TaskFilters['donePeriod']> = ['24h', '7d', '30d', 'all'];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-9 gap-1.5 border border-raios-elevated/40',
            value !== '24h' && 'border-raios-accent/40 text-raios-accent-text',
          )}
        >
          Done: {PERIOD_LABEL[value]}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1">
        <ul className="flex flex-col gap-0.5">
          {periods.map((p) => (
            <li key={p}>
              <button
                onClick={() => onChange(p)}
                className={cn(
                  'w-full text-left px-2 py-1.5 rounded text-xs',
                  p === value ? 'bg-raios-accent/15 text-raios-accent-text' : 'text-raios-text hover:bg-raios-elevated/50',
                )}
              >
                {PERIOD_LABEL[p]}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

interface ActiveChipProps {
  label: string;
  onRemove: () => void;
}

function ActiveChip({ label, onRemove }: ActiveChipProps) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-raios-accent/10 text-raios-accent-text border border-raios-accent/30 text-[11px]">
      {label}
      <button onClick={onRemove} className="hover:text-raios-text" aria-label={`Remover filtro ${label}`}>
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}
